import { cn } from "@reactive-resume/utils";
import type { VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

import { inputVariants } from "../variants/input";

type InputVariantProps = Omit<VariantProps<typeof inputVariants>, "hasError">;

export type InputProps = {
  hasError?: boolean;
} & InputVariantProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">;

/**
 * Single-line text input with shared size + radius variants.
 *
 * Use `size="sm" | "md" | "lg"` instead of overriding `h-*` via className,
 * so every form across the app stays visually consistent. Add a new size
 * to `inputVariants` when a brand-new shape is needed; never inline `h-11`.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size = "md", hasError = false, ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      autoComplete="off"
      className={cn(
        inputVariants({ size, hasError }),
        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        "file:border-0 file:bg-transparent file:pt-1 file:text-sm file:font-medium file:text-primary",
        className,
      )}
      {...props}
    />
  ),
);

Input.displayName = "Input";
